import React, { useLayoutEffect, useRef, useState } from "react";
import { useCreateMessageMutation } from "../../../api/chatApi";
import { ChatRequestTaskQue } from "../../../api/chatRequestTaskQueue";
import type { Message } from "./types";
import { preventOverflowOnTextarea } from "../../../products/copy-assistant/utils";
import { Button } from "../../../components/tailwind";

type ChatMessageFormProps = {
  chatId: number;
  chatRequestTaskQue: ChatRequestTaskQue;
  modelName: string;
  onMessageAdded: (newMessage: Message) => void;
};

export const ChatMessageForm: React.FC<ChatMessageFormProps> = ({
  chatId,
  chatRequestTaskQue,
  modelName,
  onMessageAdded,
}) => {
  const [inputText, setInputText] = useState<string>("");
  const [createMessage, { isLoading }] = useCreateMessageMutation();
  const textareaRef = useRef<HTMLTextAreaElement>();

  useLayoutEffect(() => {
    if (textareaRef.current) {
      preventOverflowOnTextarea(textareaRef.current);
    }
  }, []);

  const handleSubmit = async (event: React.FormEvent): Promise<void> => {
    event.preventDefault();
    if (!inputText.trim() || isLoading) return;

    setInputText("");

    try {
      const response = await createMessage({
        chat_id: chatId,
        text: inputText,
      }).unwrap();

      const createdMessage = response.created_message;
      const valueError = response.value_error;
      const error = response.error;

      if (error) {
        throw new Error(error);
      } else if (valueError) {
        throw new Error(valueError);
      }

      const userMessage: Message = {
        role: "user",
        text: createdMessage.text,
        timeStamp: createdMessage.timestamp,
      };
      onMessageAdded(userMessage);

      const { response_message } = await chatRequestTaskQue.requestResponse(
        chatId
      );

      if (!response_message) {
        throw new Error("No response message received from chat");
      }

      const assistantMessage: Message = {
        role: "assistant",
        text: response_message.text,
        timeStamp: response_message.timestamp,
      };
      onMessageAdded(assistantMessage);
    } catch (error) {
      console.error("Error sending message:", error);
    }
  };

  return (
    <form
      onSubmit={handleSubmit}
      className="tw-grow-0 tw-p-4 tw-rounded-md tw-border tw-shadow-lg"
    >
      <textarea
        value={inputText}
        ref={textareaRef}
        onChange={(e) => setInputText(e.target.value)}
        className="tw-m-1 !tw-p-0 tw-resize-none tw-border !tw-ring-0"
        disabled={isLoading}
      />
      <div className="tw-flex tw-justify-between">
        <div className="tw-flex tw-gap-4">
          <button>Attach File</button>
          <select>
            <option key={modelName} value={modelName}>
              {modelName}
            </option>
          </select>
        </div>
        <Button
          type="submit"
          disabled={inputText.length === 0 || isLoading}
          variant="textual-blue"
        >
          {isLoading ? "Sending..." : "Send"}
        </Button>
      </div>
    </form>
  );
};
